import React, { Key, ReactNode } from 'react';
import { GlobalDOMAttributes, MultipleSelection } from '@react-types/shared';
import {
  Collection,
  Header,
  MenuSection as AriaMenuSection,
  type MenuSectionProps as AriaMenuSectionProps,
} from 'react-aria-components';

import styles from '@danske/sapphire-css/components/listbox/listbox.module.css';
import clsx from 'clsx';
import { Separator } from '../..';

export interface MenuSectionProps<T extends object>
  extends Pick<
      AriaMenuSectionProps<T>,
      | 'id'
      | 'items'
      | 'children'
      | 'aria-label'
      | keyof GlobalDOMAttributes<HTMLElement>
    >,
    Pick<MultipleSelection, 'selectionMode' | 'disallowEmptySelection'> {
  /**
   * A visible heading for the section.
   * Sections without a heading require an aria-label for accessibility.
   */
  heading?: ReactNode;

  /** The initial selected keys in the collection (uncontrolled). */
  defaultSelectedKeys?: Exclude<
    MultipleSelection['defaultSelectedKeys'],
    'all'
  >;
  /** The currently selected keys in the collection (controlled). */
  selectedKeys?: Exclude<MultipleSelection['defaultSelectedKeys'], 'all'>;
  /** Handler that is called when the selection changes. */
  onSelectionChange?: (keys: Set<Key>) => void;
}

/**
 * A MenuSection represents a section within a Menu.
 */
export function MenuSection<T extends object>({
  heading,
  items,
  children,
  selectionMode = 'none',
  ...props
}: MenuSectionProps<T>): React.JSX.Element {
  return (
    <>
      {/*
       Conditionally rendering separator based on whether the section is the first/last node would
       require access to the Node object, which in turn requires a custom section component
       (created using createBranchNode). To be able to use AriaMenuSection component, the last
       separator is made hidden using css.
      */}
      <Separator UNSAFE_className={styles['sapphire-listbox__separator']} />
      <AriaMenuSection
        {...props}
        selectionMode={selectionMode}
        className={clsx(styles['sapphire-listbox__section'], {
          [styles['sapphire-listbox--selection']]: selectionMode !== 'none',
        })}
        // select all doesn't even work in Menu but the types in RAC MenuSection accept it.
        onSelectionChange={(selection) => {
          if (selection !== 'all') {
            props.onSelectionChange?.(selection);
          }
        }}
      >
        {heading && (
          <Header className={styles['sapphire-listbox__section-header']}>
            {heading}
          </Header>
        )}
        <Collection items={items}>{children}</Collection>
      </AriaMenuSection>
    </>
  );
}
